import { z } from 'zod'

// Credit Card Schema
export const creditCardSchema = z.object({
  nameOnCard: z
    .string()
    .min( 1, `Card holder first name is required` )
    .regex( /^[a-zA-Z\s]*$/, {
      message: `Invalid name`
    }),
  CVV: z
    .string()
    .min( 3, `CVV is required` )
    .max( 4, `Invalid CVV` )
    .regex( /^\d+$/, {
      message: `Invalid CVV`
    }),
  cardNumber: z
    .string()
    .min( 1, `Card Number is required` )
    .refine( ( cc ) => {
      const cardNumber = cc.replace( /\D/g, `` )
      return cardNumber.length >= 13 && cardNumber.length <= 19
    }, `Invalid card number` ),
  expirationMonth: z
    .string()
    .min( 1, `Expiration month is required` )
    .refine( ( mm ) => {
      const month = parseInt( mm )
      return !isNaN( month ) && month >= 1 && month <= 12
    }, `Invalid expiration month` ),
  expirationYear: z
    .string()
    .min( 1, `Expiration year is required` )
    .length( 4, `Invalid expiration year` )
    .refine( ( yyyy: string ) => {
      const now = new Date()
      const currentYear = now.getFullYear()
      if ( parseInt( yyyy ) < currentYear ) {
        return false
      }

      return true
    }, `Please enter a credit card that is not expired` )
})

// Bank Account Schema - exact match to FormValues type
export const bankAccountSchema = z.object({
  routingNumber: z
    .string()
    .min( 1, `Routing number is required` )
    .regex( /^\d{9}$/, {
      message: `Invalid routing number`
    }),
  accountNumber: z
    .string()
    .min( 1, `Account number is required` )
    .regex( /^[0-9]{9,18}$/, {
      message: `Invalid account number`
    }),
  confirmAccountNumber: z
    .string()
    .min( 1, `Confirm account number is required` )
    .regex( /^[0-9]{9,18}$/, {
      message: `Invalid confirm account number`
    }),
  accountType: z
    .string()
    .min( 1, `Account type is required` ),
  nameOnAccount: z
    .string()
    .min( 1, `Name on account is required` )
    .regex( /^[a-zA-Z\s]*$/, {
      message: `Invalid name on account`
    })
})

// Define types that exactly match the schemas
export type CreditCardFormValues = z.infer<typeof creditCardSchema>
export type BankAccountFormValues = z.infer<typeof bankAccountSchema>